import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
import { getOneGrupo } from "../../../services/grupo.service";
import { getAllRegistros, eliminarRegistro } from "../../../services/registro.service"; 
import { getAllRegistrosV2, eliminarRegistroV2 } from "../../../services/registroV2.service";
import { getAllEstudiantes } from "../../../services/estudiante.service";
import { getOneTipoAdmision } from "../../../services/tipoAdmision.service";
import { EstudianteGrupoList } from "../../../modules/grupo/EstudianteGrupoList";

export const EstudiantesGrupoPage = () => {
  const { idGrupo } = useParams();
  const [grupo, setGrupo] = useState(null);
  const [estudiantes, setEstudiantes] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const grupoData = await getOneGrupo(idGrupo);
        setGrupo(grupoData);

        const [v1, v2] = await Promise.all([
          getAllRegistros().catch(() => []),
          getAllRegistrosV2().catch(() => [])
        ]);

        const registrosV1 = v1.filter(r => r.idGrupo === idGrupo && r.habilitado);
        const registrosV2 = v2.filter(r => r.idGrupo === idGrupo && r.habilitado);

        // Fusionar registros evitando duplicados por estudiante (priorizar V2)
        const registrosMap = new Map();
        
        // Primero cargamos V1
        registrosV1.forEach(r => {
          if (r.idEstudiante) registrosMap.set(r.idEstudiante.trim(), { ...r, _isV2: false });
        });
        
        // Luego V2 sobrescribe V1 si existe (es la versión más actual)
        registrosV2.forEach(r => {
          if (r.idEstudiante) {
            registrosMap.set(r.idEstudiante.trim(), {
              ...r,
              idRegistro: r.idRegistroNotasV2,
              estado: r.estadoV2,
              notaFinal: r.notaFinal,
              _isV2: true
            });
          }
        });

        const registrosGrupo = Array.from(registrosMap.values());

        const allEstudiantes = await getAllEstudiantes();

        // Mapear incluyendo idRegistro
        const listaEstudiantes = await Promise.all(
          registrosGrupo.map(async (registro) => {
            const est = allEstudiantes.find(
              (e) => e.estudiante?.idEstudiante === registro.idEstudiante
            );

            if (est) {
              let tipoAdmisionNombre = "N/A";
              if (est.estudiante?.idTipoAdmision) {
                try {
                  const resp = await getOneTipoAdmision(
                    est.estudiante.idTipoAdmision
                  );
                  tipoAdmisionNombre = resp?.nombre || "N/A";
                } catch {
                  tipoAdmisionNombre = "N/A";
                }
              }

              return {
                usuario: est.usuario,
                persona: est.persona,
                estudiante: {
                  ...est.estudiante,
                  tipoAdmision: tipoAdmisionNombre,    
                  notaFinal: registro.notaFinal ?? 0,
                  estado: registro.estado || "N/A",
                },
                registroId: registro.idRegistro
              };
            }

            return {
              usuario: { username: "Desconocido" },
              persona: {
                nombre: "Sin datos",
                apellido: "",
                celular: "",
                ci: "",
                sexo: "",
              },
              estudiante: {
                idEstudiante: registro.idEstudiante,
                credito: null,
                tipoAdmision: "N/A",
                notaFinal: registro.notaFinal ?? 0,
                estado: registro.estado || "N/A",
              },
              registroId: registro.idRegistro // ID del registro
            };
          })
        );

        setEstudiantes(listaEstudiantes);
      } catch (error) {
        console.error("Error al cargar estudiantes del grupo:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [idGrupo]);

  // Función para eliminar registro
  const handleDelete = async (registroId) => {
    try {
      const registroAEliminar = estudiantes.find(est => est.registroId === registroId);
      
      if (registroAEliminar?._isV2) {
        await eliminarRegistroV2(registroId);
      } else {
        await eliminarRegistro(registroId);
      }
      
      // Actualizar estado local
      setEstudiantes(prev => 
        prev.filter(est => est.registroId !== registroId)
      );
      
      alert("Registro eliminado correctamente");
    } catch (error) {
      console.error("Error eliminando registro:", error);
      alert("Error al eliminar el registro");
    }
  };

  const estudiantesFiltrados = estudiantes?.filter((est) => {
    const nombreCompleto = `${est.persona?.nombre || ""} ${est.persona?.apellido || ""}`.toLowerCase();
    return nombreCompleto.includes(busqueda.toLowerCase());
  });

  return (
    <ContenedorPageTemplate
      titulo={`Estudiantes del Grupo "${grupo?.nombre || ""}"`}
      objetos={estudiantes}
    >
      {loading && <p>Cargando estudiantes...</p>}
      
      {estudiantes && (
        <div className="mb-2">
          <p className="text-gray-700">
            Total de estudiantes del grupo: {estudiantes.length}
          </p>
        </div>
      )}
      
      <div className="flex gap-4 py-3">
        <input
          type="text"
          placeholder="Buscar estudiante..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="flex-1 text-black border shadow-sm rounded-xl py-3 px-4 focus:ring-1 focus:border-blue-500 focus:ring-blue-300 transition-colors hover:border-blue-500 border-slate-200"
        />
      </div>

      {estudiantes && (
        <EstudianteGrupoList
          estudiantes={estudiantesFiltrados}
          onDelete={handleDelete} // Pasar función de eliminación
        />
      )}
    </ContenedorPageTemplate>
  );
};